import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

export default function QuizEndScreen({ correct, total, wrongItems, onRetry }) {
    const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
    const scoreColor = pct >= 80 ? '#4ade80' : pct >= 50 ? '#facc15' : '#f87171';
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const bg = isDark
        ? 'linear-gradient(135deg, #0d0d1a 0%, #12122a 40%, #0a1628 100%)'
        : 'linear-gradient(135deg, #dddaf0 0%, #e0ddf2 40%, #d8e0f0 100%)';
    const cardBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.6)';
    const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(80,70,140,0.15)';
    const titleColor = isDark ? '#e8e8f0' : '#1a1830';
    const mutedColor = isDark ? '#7878a0' : '#5050a0';
    const wrongBg = isDark ? 'rgba(248,113,113,0.06)' : 'rgba(248,113,113,0.08)';
    const wrongBorder = isDark ? 'rgba(248,113,113,0.15)' : 'rgba(248,113,113,0.2)';
    const wrongRowBorder = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(80,70,140,0.08)';
    const wrongItemText = isDark ? '#c8c8d8' : '#2a2850';
    const wrongAnswerText = isDark ? '#9d97ff' : '#4a40c0';

    const scoreRgb = scoreColor === '#4ade80' ? '74,222,128' : scoreColor === '#facc15' ? '250,204,21' : '248,113,113';

    return (
        <Box sx={{ minHeight: '100vh', width: '100%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 6 }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }} style={{ width: '100%', maxWidth: 520 }}>
                <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: '24px', p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '3rem', mb: 1 }}>
                        {pct >= 80 ? '🎉' : pct >= 50 ? '💪' : '📚'}
                    </Typography>
                    <Typography sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: { xs: '1.6rem', sm: '2rem' }, color: titleColor, mb: 0.5 }}>
                        Quiz voltooid!
                    </Typography>

                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', my: 3, px: 4, py: 2, background: `rgba(${scoreRgb},0.1)`, border: `1px solid ${scoreColor}30`, borderRadius: '16px' }}>
                        <Typography sx={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '2.8rem', color: scoreColor, lineHeight: 1 }}>
                            {pct}%
                        </Typography>
                        <Typography sx={{ color: mutedColor, fontSize: '0.85rem', mt: 0.5, fontFamily: "'DM Sans', sans-serif" }}>
                            {correct} van {total} correct
                        </Typography>
                    </Box>

                    {wrongItems && wrongItems.length > 0 && (
                        <Box sx={{ textAlign: 'left', mb: 3, maxHeight: 220, overflowY: 'auto', background: wrongBg, border: `1px solid ${wrongBorder}`, borderRadius: '14px', p: 2 }}>
                            <Typography sx={{ color: '#f87171', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5, fontFamily: "'DM Sans', sans-serif" }}>
                                Fout gemaakte vragen
                            </Typography>
                            {wrongItems.map((w, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: i < wrongItems.length - 1 ? `1px solid ${wrongRowBorder}` : 'none' }}>
                                    <Typography sx={{ color: wrongItemText, fontSize: '0.88rem', fontFamily: "'DM Sans', sans-serif" }}>
                                        {w.question || w.dutch}
                                    </Typography>
                                    <Typography sx={{ color: wrongAnswerText, fontSize: '0.88rem', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", ml: 2, flexShrink: 0 }}>
                                        {w.answer || w.darija || w.darijaBase}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Button fullWidth variant="contained" onClick={onRetry} sx={{
                        borderRadius: '14px', py: 1.6, fontSize: '1rem', fontWeight: 700, fontFamily: "'DM Sans', sans-serif", textTransform: 'none',
                        background: 'linear-gradient(135deg, #6c63ff 0%, #2575fc 100%)', boxShadow: '0 4px 24px rgba(108,99,255,0.35)',
                        '&:hover': { background: 'linear-gradient(135deg, #7d75ff 0%, #3a85ff 100%)', transform: 'translateY(-1px)', boxShadow: '0 6px 28px rgba(108,99,255,0.45)' },
                        transition: 'all 0.2s ease',
                    }}>
                        Opnieuw proberen
                    </Button>
                </Box>
            </motion.div>
        </Box>
    );
}
